import axios from "axios";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import FormPageLayout from "../components/form-page-layout";
import LoadingOverlay from "../components/loading-overlay";
import Modal from "../components/modal";
import { WORK_API_URL } from "../config/enviroments";
import { formatDateWithDayOfWeek, formatTimeRange } from "../utils/date.util";
import { InterviewSlot } from "./types";
import { useAutoReload } from "./use-auto-reload";

interface GroupedSlot {
  timeRange: string;
  slotStartTime: string;
  slotEndTime: string;
  slots: InterviewSlot[];
  availableCount: number;
}

interface GroupedByDate {
  date: string;
  dateFormatted: string;
  timeSlots: GroupedSlot[];
}

const InterviewListPage: React.FC = () => {
  const [entries, setEntries] = useState<InterviewSlot[]>([]);
  const [groupedEntries, setGroupedEntries] = useState<GroupedByDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState<string | null>(null);
  const [manualReloadTrigger, setManualReloadTrigger] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<InterviewSlot | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<GroupedSlot | null>(
    null
  );
  const [registeredSlotId, setRegisteredSlotId] = useState<string | null>(null);
  const [hasRegisteredInterview, setHasRegisteredInterview] = useState(false);
  const [applicationFormIdNumber, setApplicationFormIdNumber] = useState<
    number | null
  >(null);

  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const formId = query.get("formId");
  const reloadTrigger = useAutoReload();

  // Function to group slots by date and time
  const groupSlotsByDateAndTime = (slots: InterviewSlot[]): GroupedByDate[] => {
    // First normalize dates and times to strings for consistent grouping
    const normalizedSlots = slots.map((slot) => ({
      ...slot,
      dateStr:
        typeof slot.date === "string"
          ? slot.date
          : new Date(slot.date).toISOString().split("T")[0],
      startTimeStr:
        typeof slot.slotStartTime === "string"
          ? slot.slotStartTime
          : new Date(slot.slotStartTime).toTimeString().substring(0, 5),
      endTimeStr:
        typeof slot.slotEndTime === "string"
          ? slot.slotEndTime
          : new Date(slot.slotEndTime).toTimeString().substring(0, 5),
    }));

    // Group by date string
    const groupedByDate = _.groupBy(normalizedSlots, "dateStr");

    return _.map(groupedByDate, (dateSlots, dateStr) => {
      // Then group by time range within each date
      const groupedByTime = _.groupBy(
        dateSlots,
        (slot) => `${slot.startTimeStr}-${slot.endTimeStr}`
      );

      const timeSlots: GroupedSlot[] = _.map(groupedByTime, (timeSlots) => {
        const firstSlot = timeSlots[0];
        return {
          timeRange: formatTimeRange(
            firstSlot.startTimeStr,
            firstSlot.endTimeStr
          ),
          slotStartTime: firstSlot.startTimeStr,
          slotEndTime: firstSlot.endTimeStr,
          slots: timeSlots.map((slot) => ({
            ...slot,
            // Ensure consistent format for original slot data
            date: slot.dateStr,
            slotStartTime: slot.startTimeStr,
            slotEndTime: slot.endTimeStr,
          })),
          availableCount: timeSlots.length,
        };
      });

      // Sort time slots by start time
      const sortedTimeSlots = _.sortBy(timeSlots, "slotStartTime");

      return {
        date: dateStr,
        dateFormatted: formatDateWithDayOfWeek(dateStr),
        timeSlots: sortedTimeSlots,
      };
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  useEffect(() => {
    if (!formId) {
      setEntries([]);
      setLoading(false);
      setError("Thiếu thông tin hồ sơ");
      return;
    }
    setLoading(true);
    axios
      .get(
        `${WORK_API_URL}/hr-api/v1/interview-shift-entries/${formId}/available`
      )
      .then((res) => {
        const availableSlots = _.get(res, "data.availableSlotsList", []);
        const message = _.get(res, "data.message", "");
        const code = _.get(res, "data.code", "");
        const applicationFormId = _.get(res, "data.applicationFormId", null);

        if (_.isArray(availableSlots) && availableSlots.length > 0) {
          setEntries(availableSlots);
          setGroupedEntries(groupSlotsByDateAndTime(availableSlots));
        } else {
          setEntries([]);
          setGroupedEntries([]);
        }

        if (message) {
          setError(message);
        }

        // Check if the code indicates interview has already been registered
        if (code === "INTERVIEW_ALREADY_REGISTERED") {
          setHasRegisteredInterview(true);
        }

        // Set applicationFormId if available
        if (applicationFormId) {
          setApplicationFormIdNumber(applicationFormId);
        }
      })
      .catch((err) => {
        const errorCode = _.get(err, "response.data.code", "");

        // If missing required fields error from interview API, redirect to form
        if (errorCode === "MISSING_REQUIRED_FIELDS") {
          toast.error(
            "Vui lòng điền thông tin thiếu trước khi đăng ký phỏng vấn"
          );
          navigate(`/?formId=${formId}`);
          return;
        }

        // Check if interview has already been registered
        if (errorCode === "INTERVIEW_ALREADY_REGISTERED") {
          setHasRegisteredInterview(true);
        }

        const errorMessage = _.get(
          err,
          "response.data.message",
          _.get(err, "message", "Không thể tải danh sách ca phỏng vấn")
        );
        setError(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [formId, reloadTrigger, manualReloadTrigger, navigate]);

  const handleTimeSlotClick = (timeSlot: GroupedSlot) => {
    // Select the first available slot from the time group
    const firstSlot = timeSlot.slots[0];
    setSelectedSlot(firstSlot);
    setSelectedTimeSlot(timeSlot);
    setShowConfirmModal(true);
  };

  const handleConfirmRegister = () => {
    if (selectedSlot) {
      setShowConfirmModal(false);
      handleRegister(selectedSlot);
    }
  };

  const handleRegister = async (entry: InterviewSlot) => {
    if (!formId) {
      toast.error("Thiếu thông tin hồ sơ");
      return;
    }

    setRegistering(entry.slotId);

    try {
      const response = await axios.post(
        `${WORK_API_URL}/hr-api/v1/interview-shift-entries/${formId}/register`,
        {
          slotId: entry.slotId,
        }
      );

      if (response.data.success) {
        // Clear any existing errors
        setError(null);

        // Store registered slot ID to show it differently
        setRegisteredSlotId(entry.slotId);

        // Show success modal with interview details
        if (response.data.interviewDetails) {
          // Navigate to success page with formId in URL (let success page fetch fresh data)
          navigate(`/interview/success?formId=${formId}`);
        } else {
          toast.success(
            response.data.message || "Đăng ký phỏng vấn thành công!"
          );
        }
      } else {
        toast.error(response.data.message || "Đăng ký không thành công");
      }
    } catch (err) {
      const errorMessage = _.get(
        err,
        "response.data.message",
        _.get(err, "message", "Có lỗi xảy ra, vui lòng thử lại")
      );
      const errorCode = _.get(err, "response.data.code", "") as string;

      // Handle specific error codes for redirects
      if (errorCode === "MISSING_REQUIRED_FIELDS") {
        toast.error(
          "Vui lòng điền thông tin thiếu trước khi đăng ký phỏng vấn"
        );
        navigate(`/?formId=${formId}`);
        return;
      }

      // Always use server message
      toast.error(errorMessage);
      setError(errorMessage);

      // Handle other specific error codes for additional actions
      if (errorCode === "SLOT_UNAVAILABLE") {
        // Trigger reload to get latest data from server
        setManualReloadTrigger((prev) => prev + 1);
      }
    } finally {
      setRegistering(null);
    }
  };

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 200 }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  return (
    <FormPageLayout
      title="Đăng ký ca phỏng vấn"
      formId={formId || ""}
      applicationFormIdNumber={applicationFormIdNumber}
      description="Vui lòng chọn một ca phỏng vấn phù hợp với bạn. Các ca đã đầy sẽ không thể đăng ký."
    >
      <LoadingOverlay
        show={!!registering}
        message="Đang đăng ký ca phỏng vấn..."
      />
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          <strong>Lỗi:</strong> {error}
          {hasRegisteredInterview && (
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-success"
                onClick={() => navigate(`/interview/success?formId=${formId}`)}
              >
                <i className="bi bi-calendar-check"></i> Xem lịch phỏng vấn đã
                đăng ký
              </button>
            </div>
          )}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}
      <div className="interview-schedule">
        {groupedEntries.map((dateGroup) => (
          <div key={dateGroup.date} className="mb-4">
            <h5 className="border-bottom pb-2 mb-3">
              <i className="bi bi-calendar3"></i> {dateGroup.dateFormatted}
            </h5>
            <div className="row g-3">
              {dateGroup.timeSlots.map((timeSlot, index) => {
                const isRegistering = timeSlot.slots.some(
                  (slot) => registering === slot.slotId
                );
                const isRegistered = timeSlot.slots.some(
                  (slot) => registeredSlotId === slot.slotId
                );

                return (
                  <div
                    key={`${dateGroup.date}-${index}`}
                    className="col-md-6 col-lg-4"
                  >
                    <div className="card h-100 shadow-sm">
                      <div className="card-body d-flex flex-column">
                        <h6 className="card-title mb-2">
                          <i className="bi bi-clock"></i> {timeSlot.timeRange}
                        </h6>
                        <p className="card-text text-muted mb-3">
                          Còn lại:{" "}
                          <span className="badge bg-info">
                            {timeSlot.availableCount} slot
                            {timeSlot.availableCount > 1 ? "s" : ""}
                          </span>
                        </p>
                        <div className="mt-auto">
                          <button
                            className={`btn w-100 ${isRegistered ? "btn-success" : "btn-primary"}`}
                            onClick={() => handleTimeSlotClick(timeSlot)}
                            disabled={
                              isRegistering ||
                              isRegistered ||
                              timeSlot.availableCount === 0
                            }
                          >
                            {isRegistering ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Đang xử lý...
                              </>
                            ) : isRegistered ? (
                              <>
                                <i className="bi bi-check-circle me-1"></i>
                                Đã đăng ký
                              </>
                            ) : timeSlot.availableCount === 0 ? (
                              <>
                                <i className="bi bi-x-circle me-1"></i>
                                Hết slot
                              </>
                            ) : (
                              <>
                                <i className="bi bi-calendar-plus me-1"></i>
                                Đăng ký
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {entries.length === 0 && (
        <div className="alert alert-info text-center mt-4">
          Hiện chưa có ca phỏng vấn nào khả dụng.
        </div>
      )}
      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        title="Xác nhận đăng ký"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowConfirmModal(false)}
            >
              Hủy
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirmRegister}
            >
              Xác nhận
            </button>
          </>
        }
      >
        {selectedSlot && selectedTimeSlot && (
          <div>
            <p>Bạn có chắc chắn muốn đăng ký ca phỏng vấn này?</p>
            <div className="mt-3">
              <p>
                <strong>Ngày:</strong>{" "}
                {formatDateWithDayOfWeek(selectedSlot.date)}
              </p>
              <p>
                <strong>Thời gian:</strong> {selectedTimeSlot.timeRange}
              </p>
              {selectedTimeSlot.availableCount > 1 && (
                <p className="text-muted">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    Còn lại {selectedTimeSlot.availableCount} slot cho khung giờ
                    này
                  </small>
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </FormPageLayout>
  );
};

export default InterviewListPage;
